import {
  User,
  Meal,
  Workout,
  ExerciseTemplate,
  SetInfo,
} from "../../../../../models/types";

/**
 * Normalize users into AI-ready context strings restricted to the past 7 days.
 * @param users Array of User
 * @param now Optional timestamp for testing; defaults to new Date()
 * @param opts Optional config (e.g., units)
 * @returns string[] — one narrative per user
 */
export function normalizeUsersForAI(
  users: User[],
  now: Date = new Date(),
  opts?: {
    /** Narrate lifted weight in "lb" or "kg". Default "lb". */
    weightUnits?: "lb" | "kg";
    /** If user.isMetricUnits is true and weightUnits==="lb", convert kg→lb. */
    convertFromMetric?: boolean;
  }
): string[] {
  const MS_IN_DAY = 24 * 60 * 60 * 1000;
  const windowStart = new Date(now.getTime() - 7 * MS_IN_DAY);
  const weightUnits = opts?.weightUnits ?? "lb";
  const convertFromMetric = opts?.convertFromMetric ?? true;

  const toLocalDateKey = (dt: Date) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const formatDowNum = (d: Date) => {
    // "Wednesday 22"
    const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
    return `${weekday} ${d.getDate()}`;
  };

  const isInLast7Days = (iso?: string | Date) => {
    if (!iso) return false;
    const t = new Date(iso);
    if (isNaN(+t)) return false;
    return t >= windowStart && t <= now;
  };

  const round = (n: number) => Math.round(n);

  const kgToLb = (kg: number) => kg * 2.2046226218;

  const maybeWeightForNarration = (
    raw: number | undefined,
    userPrefersMetric?: boolean
  ): number | null => {
    if (typeof raw !== "number" || !isFinite(raw) || raw <= 0) return null;

    if (weightUnits === "kg") {
      // Narrate in kg
      return round(userPrefersMetric ? raw : raw / 2.2046226218);
    } else {
      // Narrate in lb
      if (userPrefersMetric && convertFromMetric) {
        return round(kgToLb(raw));
      }
      return round(raw);
    }
  };

  const setLine = (
    set: SetInfo,
    userPrefersMetric?: boolean
  ): string | null => {
    const reps = set.current?.reps;
    const weight = set.current?.weight;

    // Skip empty sets (0/undefined)
    const hasReps = typeof reps === "number" && reps > 0;
    const hasWeight = typeof weight === "number" && weight > 0;
    if (!hasReps && !hasWeight) return null;

    const repWord = reps === 1 ? "rep" : "reps";
    const maybeW = maybeWeightForNarration(weight, userPrefersMetric);

    if (hasReps && hasWeight && maybeW !== null) {
      return `1 set of ${reps} ${repWord} at ${maybeW} ${weightUnits}`;
    }
    if (hasReps) return `1 set of ${reps} ${repWord}`;
    if (maybeW !== null) return `1 set at ${maybeW} ${weightUnits}`;
    return null;
  };

  const describeExercise = (
    ex: ExerciseTemplate,
    userFirstName: string,
    userPrefersMetric?: boolean
  ): string | null => {
    const name = ex.exercise?.name || "Unnamed exercise";
    const entries: string[] = [];

    for (const s of ex.sets ?? []) {
      const line = setLine(s, userPrefersMetric);
      if (line) entries.push(line);
    }

    if (!entries.length) return null;
    return `For ${name} ${userFirstName} did ${entries.join(", ")}.`;
  };

  const workoutsNarrative = (user: User): string[] => {
    const relevant = (user.workouts ?? []).filter((w) =>
      isInLast7Days(w.dayOfExecution)
    );

    if (!relevant.length) {
      return [`${user.name} has no logged workouts in the last 7 days.`];
    }

    // Group by day and sort ascending
    const byDay = new Map<string, { d: Date; items: Workout[] }>();
    for (const w of relevant) {
      const d = new Date(w.dayOfExecution);
      const key = toLocalDateKey(d);
      if (!byDay.has(key)) byDay.set(key, { d, items: [] });
      byDay.get(key)!.items.push(w);
    }
    const days = [...byDay.values()].sort(
      (a, b) => a.d.getTime() - b.d.getTime()
    );

    const lines: string[] = [];
    const firstName = user.name?.split(" ")[0] || user.name || "Client";
    for (const { d, items } of days) {
      for (const w of items) {
        const title = (w.template?.title || "workout").toLowerCase();
        const exs = w.template?.exercises ?? [];
        const exLines: string[] = [];

        for (const ex of exs) {
          const line = describeExercise(ex, firstName, user.isMetricUnits);
          if (line) exLines.push(line);
        }

        if (exLines.length) {
          lines.push(
            `${user.name} did ${title} on ${formatDowNum(d)}. ${exLines.join(
              " "
            )}`
          );
        } else {
          lines.push(`${user.name} logged ${title} on ${formatDowNum(d)}.`);
        }
      }
    }
    return lines;
  };

  const mealsNarrative = (user: User): string[] => {
    const relevant = (user.meals ?? []).filter((m) =>
      isInLast7Days(m.dateTime)
    );
    if (!relevant.length) {
      return [`${user.name} has no logged meals in the last 7 days.`];
    }

    // Group by day
    const byDay = new Map<string, { d: Date; items: Meal[] }>();
    for (const m of relevant) {
      const d = new Date(m.dateTime);
      const key = toLocalDateKey(d);
      if (!byDay.has(key)) byDay.set(key, { d, items: [] });
      byDay.get(key)!.items.push(m);
    }

    const days = [...byDay.values()].sort(
      (a, b) => a.d.getTime() - b.d.getTime()
    );

    const lines: string[] = [];
    for (const { d, items } of days) {
      let kcals = 0,
        C = 0,
        F = 0,
        P = 0;

      const itemLines = items
        .sort(
          (a, b) =>
            new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
        )
        .map((m) => {
          kcals += m.calories ?? 0;
          C += m.carbs ?? 0;
          F += m.fats ?? 0;
          P += m.protein ?? 0;
          return `${m.name} (${round(m.calories ?? 0)} kcal, ${round(
            m.carbs ?? 0
          )}C/${round(m.fats ?? 0)}F/${round(m.protein ?? 0)}P)`;
        });

      lines.push(
        `On ${formatDowNum(d)} meals: ${itemLines.join("; ")}. Totals: ${round(
          kcals
        )} kcal, ${round(C)}C/${round(F)}F/${round(P)}P.`
      );
    }
    return lines;
  };

  const buildUserContext = (u: User): string => {
    const header = `${u.name} — last 7 days summary:\n`;
    const wLines = workoutsNarrative(u);
    const mLines = mealsNarrative(u);

    return [
      header.trim(),
      "",
      "Diet:",
      ...mLines,
      "",
      "Workouts:",
      ...wLines,
    ].join("\n");
  };

  return users.map(buildUserContext);
}
